import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';
import { isBefore, parseISO } from 'date-fns';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import type { EspState } from '@/store/esp/state.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import type { AppState } from '@/store/store.ts';
import { createWeakAppSelector as createAppSelector } from '@/store/storeMethods.ts';
import type { AccumulatorScheduleLineResponse } from '@/store/targetAccumulator/api/tradeCapture/response.model.ts';
import type {
  FxTargetAccumulator,
  TargetAccumulatorQuote,
  TargetAccumulatorRfsStatus,
  TcKey,
} from '@/store/targetAccumulator/state.ts';
import type { TcValidation } from '@/store/types.ts';
import { convertPointsToRate } from '@/utils/number-point.ts';

export const selectTargetAccumulatorTile = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId] as FxTargetAccumulator | undefined,
  (targetAccumulator) => targetAccumulator,
);

export const selectTargetAccumulatorValidation = createAppSelector(
  (state: AppState, tileId: string) => selectTargetAccumulatorTile(state, tileId),
  (targetAccumulator) =>
    targetAccumulator?.tradeCapture.status === 'CHECKED' ? targetAccumulator.tradeCapture.validation : undefined,
);

export const selectTargetAccumulatorSettlementFrerquencyData = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.settlementFrequency,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.numberOfSettlements,
  (settlementFrequency, numberOfSettlements) => ({
    settlementFrequency: settlementFrequency ?? 'Daily',
    numberOfSettlements,
  }),
);
export const selectTargetAccumulatorSettlementModeData = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.settlementMode,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.settlementModeList,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.cashSettlementCurrency,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.cashSettlementCurrencyList,
  (settlementMode, settlementModeList, cashSettlementCurrency, cashSettlementCurrencyList) => ({
    settlementMode,
    settlementModeList,
    cashSettlementCurrency,
    cashSettlementCurrencyList,
  }),
);

export const selectTargetAccumulatorisCrossedData = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.isCrossed,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.crossCurrency,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.crossCurrenciesList,
  (isCrossed, crossCurrency, crossCurrenciesList) => ({
    isCrossed,
    crossCurrency,
    crossCurrenciesList,
  }),
);

export const selectTargetAccumulatorFixingReference1Data = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.fixingReference1,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.fixingReference1Default,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.fixingReference1List,
  (fixingReference1, fixingReference1Default, fixingReference1List) => ({
    fixingReference1,
    fixingReference1Default,
    fixingReference1List,
  }),
);

export const selectTargetAccumulatorFixingReference2Data = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.fixingReference2,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.fixingReference2Default,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.fixingReference2List,
  (fixingReference2, fixingReference2Default, fixingReference2List) => ({
    fixingReference2,
    fixingReference2Default,
    fixingReference2List,
  }),
);

export const selectTargetAccumulatorCustomFields = createAppSelector(
  (state: AppState, tileId: string) => selectTargetAccumulatorTile(state, tileId),
  (targetAccumulator) => targetAccumulator?.properties.customFields,
);

export const selectTargetAccumulatorScheduleData = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.schedule,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.scheduleCustomFields,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.scheduleIds,
  (schedule, scheduleCustomFields, scheduleIds) => ({
    schedule,
    scheduleCustomFields,
    scheduleIds,
  }),
);

export const selectTargetAccumulatorFirstFixing = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.firstFixingDate,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.firstFixingTenor,
  (firstFixingDate, firstFixingTenor) => ({
    firstFixingDate,
    firstFixingTenor: isNotDefined(firstFixingTenor) ? undefined : firstFixingTenor,
  }),
);

export const selectTargetAccumulatorLastFixing = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.lastFixingDate,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.lastFixingTenor,
  (lastFixingDate, lastFixingTenor) => ({
    lastFixingDate,
    lastFixingTenor: isNotDefined(lastFixingTenor) ? undefined : lastFixingTenor,
  }),
);

export const selectTargetAccumulatorAccuType = createAppSelector(
  (state: AppState, tileId: string) => selectTargetAccumulatorTile(state, tileId),
  (targetAccumulator) => targetAccumulator?.properties.accuType ?? 'TargetAccu',
);

export const selectTargetAccumulatorCurrencyData = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.currencyPair,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.notionalCurrencyIndex,
  (currencyPair, notionalCurrencyIndex) => {
    if (isNotDefined(currencyPair)) {
      return {
        notionalCurrencyIndex,
        currency: undefined,
      };
    }

    const [currency1, currency2] = currencyPair.split('/');
    const currency = notionalCurrencyIndex === 1 ? currency1 : currency2;

    return {
      notionalCurrencyIndex,
      currency,
    };
  },
);

export const selectTargetAccumulatorTradeCaptureError = createAppSelector(
  (state: AppState, tileId: string) => selectTargetAccumulatorTile(state, tileId),
  (targetAccumulator) =>
    targetAccumulator?.tradeCapture?.status === 'ERROR' ? targetAccumulator.tradeCapture.error : undefined,
);

export const selectTargetAccumulatorHedgeType = createAppSelector(
  (state: AppState, tileId: string) => selectTargetAccumulatorTile(state, tileId),
  (targetAccumulator) => targetAccumulator?.properties.hedgeType,
);

export const selectTargetAccumulatorCurrencyPair = createAppSelector(
  (state: AppState, tileId: string) => selectTargetAccumulatorTile(state, tileId),
  (targetAccumulator) => targetAccumulator?.properties.currencyPair,
);

export const selectTargetAccumulatorPremiumDateData = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.premiumDate,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.premiumDateTenor,
  (premiumDate, premiumDateTenor) => ({
    premiumDate: premiumDate ?? undefined,
    premiumDateTenor: premiumDateTenor ?? undefined,
  }),
);

export const selectTargetAccumulatorSummary = createAppSelector(
  (state: AppState, tileId: string) => selectTargetAccumulatorTile(state, tileId),
  (targetAccumulator) => targetAccumulator?.properties.summary,
);

export const selectTargetAccumulatorTotalNotional = createAppSelector(
  (state: AppState, tileId: string) => selectTargetAccumulatorTile(state, tileId),
  (targetAccumulator) => targetAccumulator?.properties.totalNotional,
);

export const selectTargetAccumulatorTotalLeverage = createAppSelector(
  (state: AppState, tileId: string) => selectTargetAccumulatorTile(state, tileId),
  (targetAccumulator) => targetAccumulator?.properties.totalLeverage,
);

export const selectTargetAccumulatorScheduleTable = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.schedule,
  (_state: AppState, _tileId: string, legId: string) => legId,
  (schedule, legId) => schedule?.[legId],
);

export const selectTargetAccumulatorHedgeData = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.hedgeCurrencyIndex,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.hedgeAmount,
  (hedgeCurrencyIndex, hedgeAmount) => ({
    hedgeCurrencyIndex: hedgeCurrencyIndex ?? undefined,
    hedgeAmount: hedgeAmount ?? undefined,
  }),
);

export const selectTargetAccumulatorOrderedScheduleItemIds = createAppSelector(
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.scheduleIds,
  (state: AppState, tileId: string) => state.targetAccumulator[tileId]?.properties.schedule,
  (scheduleIds, schedule) => {
    if ((scheduleIds?.length ?? 0) === 0 || isNotDefined(schedule)) {
      return [];
    }
    const getOrderableItems = Object.fromEntries((scheduleIds ?? []).map((id) => [id, schedule[id]?.fixingDate]));

    return Object.keys(getOrderableItems).sort(
      (id1, id2) => getOrderableItems[id1]?.localeCompare(getOrderableItems[id2] ?? '') ?? 1,
    );
  },
);

export const selectTargetAccumulatorScheduleFieldValidation = createAppSelector(
  (state: AppState, tileId: string, _legId: string, _faultyValue: keyof TcKey<AccumulatorScheduleLineResponse>) =>
    selectTargetAccumulatorValidation(state, tileId),
  (_state: AppState, _tileId: string, legId: string) => legId,
  (_state: AppState, _tileId: string, _legId: string, faultyValue: keyof TcKey<AccumulatorScheduleLineResponse>) =>
    faultyValue,
  (validations, legId, faultyValue): { validation: TcValidation[] } => {
    const scheduleValidation = validations?.schedule?.[legId];

    const validation = scheduleValidation?.[faultyValue];
    return { validation: isDefined(validation) ? [validation] : [] };
  },
);

interface CurrencyPairStuff {
  currencyPair?: string;
  currencyPairPrecision: number;
}

export const selectCurrencyPairInfo = createAppSelector(
  [(state: AppState) => state, (_state, tileId: string) => tileId],
  (state, tileId): CurrencyPairStuff => {
    const currencyPair = state.targetAccumulator[tileId]?.properties.currencyPair ?? undefined;

    return {
      currencyPair,
      currencyPairPrecision: selectCurrencyPairPrecision(state, currencyPair),
    };
  },
);

function selectEsp(state: AppState): EspState {
  return state.esp;
}

function selectTiering(state: AppState, tileId: string): string | undefined {
  const tile = state.targetAccumulator[tileId];
  return tile?.properties.tiering;
}

export const selectTargetAccumulatorEspSpot = createAppSelector(
  [selectCurrencyPairInfo, selectEsp, selectTiering],
  ({ currencyPair }, esp, tiering) => {
    if (isNotDefined(currencyPair) || isNotDefined(tiering)) {
      return undefined;
    }
    const espStreamId = getEspStreamId(currencyPair, tiering);
    const espPrice = esp.streams[espStreamId];

    if (isNotDefined(espPrice) || espPrice.status !== 'PRICED' || espPrice.priceType !== 'ESP.TRADER.PRICE') {
      return undefined;
    }

    const precision = espPrice.currencyPairPrecision;

    return convertPointsToRate(espPrice.midRate, precision);
  },
);

//██████╗ ███████╗███████╗
//██╔══██╗██╔════╝██╔════╝
//██████╔╝█████╗  ███████╗
//██╔══██╗██╔══╝  ╚════██║
//██║  ██║██║     ███████║
//╚═╝  ╚═╝╚═╝     ╚══════╝

export function isPriceable(targetAccumulator: FxTargetAccumulator): boolean {
  return targetAccumulator.tradeCapture.status === 'CHECKED' && targetAccumulator.tradeCapture.isReadyToPrice;
}

export function getTargetAccumulatorQuote(targetAccumulator: FxTargetAccumulator) {
  if (
    targetAccumulator?.properties.rfs?.status === 'PRICING' ||
    targetAccumulator?.properties.rfs?.status === 'EXPIRED'
  ) {
    return targetAccumulator.properties.rfs.quote;
  }
}

export function selectTargetAccumulatorRfsSpotHedge(targetAccumulator: FxTargetAccumulator) {
  if (
    targetAccumulator?.properties.rfs?.status === 'PRICING' ||
    targetAccumulator?.properties.rfs?.status === 'EXPIRED'
  ) {
    return targetAccumulator.properties.rfs.quote.hedge?.rateMid;
  }
}

export function selectTargetAccumulatorRfsHedgeAmount(targetAccumulator: FxTargetAccumulator) {
  if (
    targetAccumulator?.properties.rfs?.status === 'PRICING' ||
    targetAccumulator?.properties.rfs?.status === 'EXPIRED'
  ) {
    return targetAccumulator.properties.rfs.quote.hedge?.notionalAmount;
  }
}

export function getTargetAccumulatorIsEligibleToPremiumRateStoMid(targetAccumulator: FxTargetAccumulator) {
  if (
    targetAccumulator?.properties.rfs?.status === 'PRICING' ||
    targetAccumulator?.properties.rfs?.status === 'EXPIRED'
  ) {
    return targetAccumulator.properties.rfs.quote.isEligibleToPremiumRateStoMid;
  }
}

export function getTargetAccumulatorMarginData(targetAccumulator: FxTargetAccumulator) {
  if (
    targetAccumulator?.properties.rfs?.status === 'PRICING' ||
    targetAccumulator?.properties.rfs?.status === 'EXPIRED'
  ) {
    return targetAccumulator.properties.rfs.quote.marginData;
  }
}

export function getTargetAccumulatorMarketData(targetAccumulator: FxTargetAccumulator) {
  if (
    targetAccumulator?.properties.rfs?.status === 'PRICING' ||
    targetAccumulator?.properties.rfs?.status === 'EXPIRED'
  ) {
    const quote = targetAccumulator.properties.rfs.quote;
    return {
      forward: quote.forward,
      swapPoints: quote.swapPoints,
      volatility: quote.volatility,
      delta: [quote.greeks.deltaCcy1, quote.greeks.deltaCcy2],
      vega: [quote.greeks.vegaCcy1, quote.greeks.vegaCcy2],
      premiumRateStoCcyMid: [quote.premiumData?.premiumRateStoCcy1Mid, quote.premiumData?.premiumRateStoCcy2Mid],
      premiumGappedCcy: [quote.premiumData?.premiumGappedCcy1Mid, quote.premiumData?.premiumGappedCcy2Mid],
    };
  }
}

export function getTargetAccumulatorRfsId(targetAccumulator: FxTargetAccumulator): string | undefined {
  if (
    targetAccumulator.properties.rfs.status === 'PRICING' ||
    targetAccumulator.properties.rfs.status === 'AWAITING' ||
    targetAccumulator.properties.rfs.status === 'ERROR'
  ) {
    return targetAccumulator.properties.rfs.rfsId;
  }
}

export function isPremiumAmountNegative(targetAccumulator: FxTargetAccumulator): boolean {
  const premiumData = getTargetAccumulatorRfsPremiumData(
    targetAccumulator.properties.rfs.status === 'PRICING' ? targetAccumulator.properties.rfs.quote : undefined,
  );
  if (!premiumData?.premiumAmount) {
    return false;
  }

  const premiumAmountData = premiumData.premiumAmount[targetAccumulator.properties.premiumTypeCurrencyIndex - 1];
  return (premiumAmountData ?? 0) > 0 || !targetAccumulator.properties.isPriceable;
}

function isRfsStatusNotExecutable(targetAccumulator: FxTargetAccumulator, isPriceButton?: boolean): boolean {
  return isPriceButton ? isPremiumAmountNegative(targetAccumulator) : false;
}

export function selectTargetAccumulatorRfsStatus(
  targetAccumulator: FxTargetAccumulator,
  isPriceButton?: boolean,
): TargetAccumulatorRfsStatus {
  if (!areAllExpiryDatesValid(targetAccumulator)) {
    return 'NOT_PRICEABLE';
  }

  const rfsStatus = targetAccumulator.properties.rfs.status;

  switch (rfsStatus) {
    case 'ERROR': {
      const isBypassable = targetAccumulator.properties.rfs.error.multipassErrorCode === 'RrCreditCheckFailed';
      return isBypassable ? 'BYPASSABLE_ERROR' : 'ERROR';
    }

    case 'CANCELLED':
    case 'EXPIRED':
      return isPriceable(targetAccumulator) ? 'PRICEABLE' : 'NOT_PRICEABLE';

    case 'AWAITING':
      return targetAccumulator.properties.rfs.quoteType === 'Tradeable' ? 'AWAITING_EXECUTABLE' : 'AWAITING_INDICATIVE';

    case 'PRICING': {
      if (isRfsStatusNotExecutable(targetAccumulator, isPriceButton)) {
        return 'NOT_PRICEABLE';
      }
      const quoteType =
        targetAccumulator.properties.rfs.quoteType === 'Tradeable' ? 'STREAMING_EXECUTABLE' : 'STREAMING_INDICATIVE';
      return quoteType;
    }

    default:
      return assertUnreachable(rfsStatus, 'Unhandled stream status');
  }
}

export function areAllExpiryDatesValid(targetAccumulator: FxTargetAccumulator) {
  const maxDate = targetAccumulator.properties.maxDate;
  if (isNotDefined(maxDate)) {
    return true;
  }

  const expiry = targetAccumulator.properties.expiryDate;
  return isNotDefined(expiry) || isBefore(parseISO(expiry), maxDate);
}

export function getTargetAccumulatorQuoteType(
  targetAccumulator: FxTargetAccumulator,
): 'Indicative' | 'Tradeable' | 'Withdraw' {
  if (targetAccumulator.properties.rfs.status === 'PRICING' || targetAccumulator.properties.rfs.status === 'AWAITING') {
    return targetAccumulator.properties.rfs.quoteType;
  }
  return 'Indicative';
}

export interface TargetAccumulatorRfsPremiumData {
  premiumAmount: readonly [number, number];
  premiumPercent: readonly [number, number];
  premiumRateSto: readonly [number, number];
  premiumGappedCcy: readonly [number, number];
}

export function getTargetAccumulatorRfsPremiumData(
  quote: TargetAccumulatorQuote | undefined,
): TargetAccumulatorRfsPremiumData | undefined {
  if (isNotDefined(quote?.premiumData)) {
    return undefined;
  }

  return {
    premiumAmount: [quote.premiumData.premiumCcy1.ask, quote.premiumData.premiumCcy2.ask] as const,
    premiumPercent: [quote.premiumData.premiumPctCcy1, quote.premiumData.premiumPctCcy2] as const,
    premiumRateSto: [quote.premiumData.premiumRateStoCcy1Mid, quote.premiumData.premiumRateStoCcy2Mid] as const,
    premiumGappedCcy: [quote.premiumData.premiumGappedCcy1Mid, quote.premiumData.premiumGappedCcy2Mid] as const,
  };
}

export function getTargetAccumulatorRfsQuoteId(targetAccumulator: FxTargetAccumulator): string | undefined {
  if (isNotDefined(targetAccumulator.properties.rfs)) {
    return undefined;
  }

  return targetAccumulator.properties.rfs.status === 'PRICING' ? targetAccumulator.properties.rfs.quoteId : undefined;
}

export function getTargetAccumulatorTraderId(option: FxTargetAccumulator): string | undefined {
  if (option.properties.rfs.status === 'AWAITING' || option.properties.rfs.status === 'PRICING') {
    return option.properties.rfs.traderId;
  }
}

export function getTargetAccumulatorContribution(targetAccumulator: FxTargetAccumulator): 'Automatic' | 'Manual' {
  if (
    targetAccumulator?.properties.rfs.status === 'PRICING' ||
    targetAccumulator?.properties.rfs.status === 'EXPIRED'
  ) {
    return targetAccumulator.properties.rfs.quote.quoteContribution;
  }
  return 'Manual';
}

export const selectDisplayMode = createAppSelector(
  (state: AppState, tileId: string) => selectTargetAccumulatorTile(state, tileId),
  (targetAccumulator) => targetAccumulator?.properties.uiState.displayMode ?? 'Pricer',
);
